import {
    ChangeDetectionStrategy,
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    OnInit,
    signal,
    viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BaasAppState, BaasConnectivity, BaasSecurityLevel } from '@lvflanders/bar';
import { _BarInternalLayoutService, BarLayout, BarLayoutService } from '@lvflanders/bar-ng-ui';
import { BarChatLvConnectConfig } from '@lvflanders/bar-ui';
import {
    StartschermHelpWidget,
    StartschermTestProfileWidget,
    StartschermUserWidget,
    StartschermV2StateService,
    StartschermVoorwieWidget
} from '@lvflanders/startscherm-ng-ui';

import { BaasContainerBurgerprofielService } from '../burgerprofiel/baas-container-burgerprofiel.service';

@Component({
    selector: 'bar-container',
    templateUrl: './bar-container.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [BarLayout, StartschermTestProfileWidget, StartschermVoorwieWidget, StartschermUserWidget, StartschermHelpWidget]
})
/** @deprecated Use BaasContainer with OnPush change detection. */
export class BarContainer implements OnInit {
    readonly state = inject(BaasAppState);
    readonly #layout = inject(BarLayoutService);
    readonly #burgerprofiel = inject(BaasContainerBurgerprofielService);
    readonly #internalLayout = inject(_BarInternalLayoutService);
    readonly #destroyRef = inject(DestroyRef);
    readonly #startschermStateService = inject(StartschermV2StateService);

    readonly chat = viewChild<ElementRef<HTMLElement>>('chat');

    readonly withRouting = input<boolean>(true);
    readonly globalHeaderFooter = input<boolean>(true);

    touch: boolean;
    mobileFriendly: boolean;
    compact: boolean;
    hidden: boolean;
    forceHideBox: boolean;
    connectivityOffline: boolean;
    hideAll = true;
    showTestProfielen: boolean;
    showVoorwie: boolean;
    classicWidgetsModus: boolean;
    chatbotConfig: { enabled: boolean; lvConnect: BarChatLvConnectConfig };

    protected readonly helpLink = signal<string>(undefined);

    readonly #helpLink = computed(() => this.helpLink() || this.#startschermStateService.helpLink());

    constructor() {
        effect(() => {
            // trigger burgerprofiel contact logic whenever common help link changes
            this.#burgerprofiel.defineContacts(this.#helpLink());
        });
    }

    ngOnInit(): void {
        // in case we don't have the vl global variable we are running with the old widgets
        this.classicWidgetsModus = !this.#burgerprofiel.visible;
        // no layout box in case burgerprofiel widgets are used
        this.forceHideBox = this.#burgerprofiel.visible;

        this.#layout.touch$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((touch) => {
            this.#internalLayout.queueAction(() => {
                this.touch = touch;
            });
        });
        this.#layout.mobileFriendly$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((mobileFriendly) => {
            this.mobileFriendly = mobileFriendly;
        });
        this.#layout.compact$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((compact) => {
            this.#internalLayout.queueAction(() => {
                this.compact = compact;
            });
        });
        this.#layout.helpLink$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((helpLink) => {
            this.#internalLayout.queueAction(() => {
                this.helpLink.set(helpLink);
            });
        });

        this.#layout.hidden$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((hidden) => {
            this.hidden = hidden;
        });
        this.connectivityOffline = this.state.connectivity === BaasConnectivity.Offline;
        let isPrd: boolean;
        let isIntern: boolean;
        // state.toepassing is needed for all startscherm utilities
        if (this.state.toepassing) {
            isIntern = this.state.toepassing.zone === 'I';
            isPrd = this.state.toepassing.securityLevel === BaasSecurityLevel.Production;
            this.hideAll = false;
        } else {
            this.hideAll = true;
            isIntern = true;
            isPrd = false;
        }

        this.showTestProfielen = isIntern && !isPrd;
        this.showVoorwie = !isIntern;
        const chatbotConfig = this.state.config?.baasStatic?.chatbot;
        this.chatbotConfig = {
            enabled:
                (chatbotConfig?.enabled || chatbotConfig.enabledAppCodes?.indexOf(this.state.toepassing.code) > -1) &&
                chatbotConfig.disabledAppCodes?.indexOf(this.state.toepassing.code) == -1,
            lvConnect: chatbotConfig?.lvConnect
        };
    }

    onCompactChange(compact: boolean) {
        this.#layout.compact$.next(compact);
    }
}
